import React, { useState, useEffect, useCallback, useMemo } from 'react';

import store from 'store';

import * as api from 'api/methods';

import {
  fetchAllCoursesForLessonAttach,
  type CourseLessonRow,
  type LinkedCourse,
} from 'store/courses-lessons/helpers';

import { getTranslation } from 'helpers/getTranslation';

import { getCourseType } from 'components/CoursesTable/CoursesTable.helpers';

import LessonReplaceInCourseDrawer from './LessonReplaceInCourseDrawer';

import {
  buildCourseLessonOptions,
  fetchModulesForCourse,
  groupCourseLessonsByModule,
  isModularCourse,
  type CourseLessonOption,
  type CourseModuleOption,
} from './LessonReplaceInCourseDrawer.helpers';

type CourseWithTypeLabel = LinkedCourse & {
  typeLabel?: string;
};

const getApiErrorMessage = (error: unknown): string => {
  const responseData = (error as any)?.response?.data;

  if (responseData?.text) {
    return responseData.text;
  }

  if (responseData?.title) {
    return responseData.title;
  }

  if (responseData?.code === 'LESSON_MULTI_COURSE') {
    return getTranslation(
      'courses_lessons_page.replace_drawer.errors.multi_course',
    );
  }

  if (responseData?.code === 'LESSON_CROSS_SCHOOL') {
    return getTranslation(
      'courses_lessons_page.replace_drawer.errors.cross_school',
    );
  }

  return getTranslation('courses_lessons_page.replace_drawer.errors.generic');
};

const LessonReplaceInCourseDrawerContainer = ({
  open,
  lesson,
  onClose,
  onReplaceComplete,
}: {
  open: boolean;
  lesson: CourseLessonRow | null;
  onClose: () => void;
  onReplaceComplete: () => void | Promise<void>;
}) => {
  const [loading, setLoading] = useState(false);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [courses, setCourses] = useState<CourseWithTypeLabel[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedReplacedLessonId, setSelectedReplacedLessonId] = useState<
    string | null
  >(null);
  const [courseLessons, setCourseLessons] = useState<CourseLessonOption[]>([]);
  const [courseModules, setCourseModules] = useState<CourseModuleOption[]>([]);

  const resetReplaceState = useCallback(() => {
    setSelectedCourseId(null);
    setSelectedReplacedLessonId(null);
    setCourseLessons([]);
    setCourseModules([]);
    setErrorMessage(null);
  }, []);

  const loadCourseLessons = useCallback(async (course: LinkedCourse) => {
    setLessonsLoading(true);
    setSelectedReplacedLessonId(null);
    setCourseLessons([]);
    setCourseModules([]);

    try {
      const lessonsResponse = await api.getCourseLessons(course.id);
      const lessonList = Array.isArray(lessonsResponse)
        ? lessonsResponse
        : lessonsResponse?.data ?? [];

      if (isModularCourse(course)) {
        const modules = await fetchModulesForCourse(course);

        setCourseModules(modules);
      }

      setCourseLessons(
        buildCourseLessonOptions(lessonList).filter(
          (courseLesson) => courseLesson.id !== lesson?.id,
        ),
      );
    } catch (error) {
      console.warn(error);
      setCourseLessons([]);
      setCourseModules([]);
    }

    setLessonsLoading(false);
  }, [lesson?.id]);

  useEffect(() => {
    if (!open || !lesson) {
      return;
    }

    resetReplaceState();

    const loadCourses = async () => {
      setLoading(true);

      try {
        const allCourses = await fetchAllCoursesForLessonAttach(
          store.getState,
        );

        setCourses(
          allCourses.map((course) => ({
            ...course,
            typeLabel: getCourseType(course.type),
          })),
        );
      } catch (error) {
        console.warn(error);
        setCourses([]);
      }

      setLoading(false);
    };

    void loadCourses();
  }, [lesson, open, resetReplaceState]);

  const handleCourseSelect = useCallback(
    (courseId: string) => {
      if (saving || loading) {
        return;
      }

      setErrorMessage(null);
      setSelectedCourseId(courseId);

      const selectedCourse = courses.find((course) => course.id === courseId);

      if (selectedCourse) {
        void loadCourseLessons(selectedCourse);
      }
    },
    [courses, loadCourseLessons, loading, saving],
  );

  const handleReplacedLessonSelect = useCallback(
    (lessonId: string) => {
      if (saving || loading || lessonsLoading) {
        return;
      }

      setErrorMessage(null);
      setSelectedReplacedLessonId(lessonId);
    },
    [lessonsLoading, loading, saving],
  );

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === selectedCourseId) ?? null,
    [courses, selectedCourseId],
  );

  const isModularLessons = Boolean(
    selectedCourse && isModularCourse(selectedCourse),
  );

  const lessonModuleGroups = useMemo(() => {
    if (!isModularLessons) {
      return null;
    }

    return groupCourseLessonsByModule(
      courseLessons,
      courseModules,
      getTranslation('courses_lessons_page.replace_drawer.unassigned_module'),
    );
  }, [courseLessons, courseModules, isModularLessons]);

  const canSave = useMemo(
    () => Boolean(selectedCourseId && selectedReplacedLessonId && lesson?.id),
    [lesson?.id, selectedCourseId, selectedReplacedLessonId],
  );

  const handleSave = useCallback(async () => {
    if (!canSave || !lesson?.id || !selectedCourseId || !selectedReplacedLessonId) {
      return;
    }

    setSaving(true);
    setErrorMessage(null);

    try {
      await api.replaceLessonInCourse({
        courseId: selectedCourseId,
        newLessonId: lesson.id,
        replacedLessonId: selectedReplacedLessonId,
      });

      await onReplaceComplete();
      onClose();
    } catch (error) {
      console.warn(error);
      setErrorMessage(getApiErrorMessage(error));
    }

    setSaving(false);
  }, [
    canSave,
    lesson?.id,
    onClose,
    onReplaceComplete,
    selectedCourseId,
    selectedReplacedLessonId,
  ]);

  const handleCancel = useCallback(() => {
    if (saving) {
      return;
    }

    onClose();
  }, [onClose, saving]);

  return (
    <LessonReplaceInCourseDrawer
      open={open}
      lessonTitle={lesson?.title ?? ''}
      loading={loading}
      lessonsLoading={lessonsLoading}
      saving={saving}
      canSave={canSave}
      errorMessage={errorMessage}
      courses={courses}
      courseLessons={courseLessons}
      isModularLessons={isModularLessons}
      lessonModuleGroups={lessonModuleGroups}
      selectedCourseId={selectedCourseId}
      selectedReplacedLessonId={selectedReplacedLessonId}
      onClose={handleCancel}
      onCancel={handleCancel}
      onSave={handleSave}
      onCourseSelect={handleCourseSelect}
      onReplacedLessonSelect={handleReplacedLessonSelect}
    />
  );
};

export default React.memo(LessonReplaceInCourseDrawerContainer) as any;
